import API_CONFIG, { buildApiUrl, buildBackendUrl, getRequestConfig, getUploadConfig } from '../config/api';

// Centralized API Service for Polymath Kids Frontend
class ApiService {
  // Generic GET request
  async get(endpoint, config = {}) {
    try {
      const url = buildApiUrl(endpoint);
      const requestConfig = getRequestConfig({
        method: 'GET',
        ...config
      });
      
      const response = await fetch(url, requestConfig);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('API GET Error:', error);
      throw error;
    }
  }

  // Generic POST request
  async post(endpoint, data = {}, config = {}) {
    try {
      const url = buildApiUrl(endpoint);
      const requestConfig = getRequestConfig({
        method: 'POST',
        body: JSON.stringify(data),
        ...config
      });
      const response = await fetch(url, requestConfig);
      if (!response.ok) {
        // Try to get plain text error
        let errorText = await response.text();
        let error = new Error(errorText);
        error.status = response.status;
        throw error;
      }
      // Try to parse JSON, fallback to text
      try {
        return await response.json();
      } catch (jsonErr) {
        return await response.text();
      }
    } catch (error) {
      console.error('API POST Error:', error);
      throw error;
    }
  }

  // Generic PUT request
  async put(endpoint, data = {}, config = {}) {
    try {
      const url = buildApiUrl(endpoint);
      const requestConfig = getRequestConfig({
        method: 'PUT',
        body: JSON.stringify(data),
        ...config
      });
      
      const response = await fetch(url, requestConfig);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('API PUT Error:', error);
      throw error;
    }
  }

  // Generic DELETE request
  async delete(endpoint, config = {}) {
    try {
      const url = buildApiUrl(endpoint);
      const requestConfig = getRequestConfig({
        method: 'DELETE',
        ...config
      });
      
      const response = await fetch(url, requestConfig);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('API DELETE Error:', error);
      throw error;
    }
  }

  // File upload request
  async uploadFile(endpoint, formData, config = {}) {
    try {
      const url = buildApiUrl(endpoint);
      const uploadConfig = getUploadConfig({
        method: 'POST',
        body: formData,
        ...config
      });
      
      const response = await fetch(url, uploadConfig);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('API Upload Error:', error);
      throw error;
    }
  }

  // Authentication Methods
  async login(credentials) {
    try {
      const response = await this.post(API_CONFIG.ENDPOINTS.LOGIN, credentials);
      return response;
    } catch (error) {
      console.error('Login Error:', error);
      throw error;
    }
  }

  async signup(userData) {
    return this.post(API_CONFIG.ENDPOINTS.SIGNUP, userData);
  }

  async signupWithFile(formData) {
    try {
      const response = await this.uploadFile(API_CONFIG.ENDPOINTS.SIGNUP, formData);
      return response;
    } catch (error) {
      console.error('Signup Error:', error);
      throw error;
    }
  }

  async logout() {
    return this.post(API_CONFIG.ENDPOINTS.LOGOUT);
  }

  async getCurrentUser() {
    return this.get(API_CONFIG.ENDPOINTS.USER);
  }

  // Student Management Methods
  async getStudentId(userId) {
    return this.get(`${API_CONFIG.ENDPOINTS.GET_STUDENT_ID}/${userId}`);
  }

  async getStudentRewards(studentId) {
    return this.get(API_CONFIG.ENDPOINTS.GET_STUDENT_REWARDS, {
      params: { studentId }
    });
  }

  async getStudentInvoices(studentId) {
    return this.get(`${API_CONFIG.ENDPOINTS.GET_STUDENT_INVOICES}/${studentId}`);
  }

  async getStudentInvoiceDetails(feeId) {
    return this.get(`${API_CONFIG.ENDPOINTS.GET_STUDENT_INVOICE_DETAILS}/${feeId}`);
  }

  async markPaymentDone(feeId, status) {
    return this.post(`${API_CONFIG.ENDPOINTS.MARK_PAYMENT_DONE}/${feeId}/${status}`);
  }

  async amountPaid(feeId, amount) {
    return this.post(`${API_CONFIG.ENDPOINTS.AMOUNT_PAID}/${feeId}/${amount}`);
  }

  // Course Management Methods
  async getMyCourses() {
    return this.get(API_CONFIG.ENDPOINTS.GET_MY_COURSES);
  }

  async getTeacherTaughtCourses() {
    return this.get(API_CONFIG.ENDPOINTS.GET_TEACHER_TAUGHT_COURSES);
  }

  async fetchStudentRegisteredCourses() {
    return this.get(API_CONFIG.ENDPOINTS.FETCH_STUDENT_REGISTERED_COURSES);
  }

  async getTeacherIdsViaClassId(classId) {
    return this.get(`${API_CONFIG.ENDPOINTS.GET_TEACHER_IDS_VIA_CLASS_ID}/${classId}`);
  }

  // User Management Methods
  async getUsers(limit = 50, page = 1) {
    return this.get(`${API_CONFIG.ENDPOINTS.GET_USERS}?limit=${limit}&page=${page}`);
  }

  async setTables(userId) {
    return this.post(`${API_CONFIG.ENDPOINTS.SET_TABLES}/${userId}`);
  }

  async updateType(userId, type) {
    return this.post(`${API_CONFIG.ENDPOINTS.UPDATE_TYPE}/${userId}`, { type });
  }

  async updateUserStatus(userId, status) {
    return this.post(`${API_CONFIG.ENDPOINTS.UPDATE_USER_STATUS}/${userId}`, { status });
  }

  async getCoursenamesByUserId(userId) {
    return this.get(`${API_CONFIG.ENDPOINTS.GET_COURSENAMES_BY_USER_ID}/${userId}`);
  }

  async getCurrentStatus(userId) {
    return this.get(`${API_CONFIG.ENDPOINTS.GET_CURRENT_STATUS}/${userId}`);
  }

  // Course Details Methods
  async getCourseName(courseId) {
    return this.get(`${API_CONFIG.ENDPOINTS.GET_COURSE_NAME}/${courseId}`);
  }

  async getModulesByCourse(courseId) {
    return this.get(`${API_CONFIG.ENDPOINTS.GET_MODULES_BY_COURSE}/${courseId}`);
  }

  async getModuleName(moduleId) {
    return this.get(`${API_CONFIG.ENDPOINTS.GET_MODULE_NAME}/${moduleId}`);
  }

  async getLessonsByModule(moduleId) {
    return this.get(`${API_CONFIG.ENDPOINTS.GET_LESSONS_BY_MODULE}/${moduleId}`);
  }

  async getLessonName(lessonId) {
    return this.get(`${API_CONFIG.ENDPOINTS.GET_LESSON_NAME}/${lessonId}`);
  }

  // Activities and Projects Methods
  async getStudentIdFromUser(userId) {
    return this.post(API_CONFIG.ENDPOINTS.GET_STUDENT_ID_FROM_USER, { UserId: userId });
  }

  async getActivityIdFromStudent(studentId) {
    return this.post(API_CONFIG.ENDPOINTS.GET_ACTIVITY_ID_FROM_STUDENT, { StudentId: studentId });
  }

  async getCourseIdFromActivityId(activityId) {
    return this.post(API_CONFIG.ENDPOINTS.GET_COURSE_ID_FROM_ACTIVITY_ID, { ActivityID: activityId });
  }

  async getStudentModuleProjectAssignment(studentId) {
    return this.post(API_CONFIG.ENDPOINTS.GET_STUDENT_MODULE_PROJECT_ASSIGNMENT, { studentId });
  }

  async getStudentLessonProjectAssignment(studentId, courseId) {
    return this.post(API_CONFIG.ENDPOINTS.GET_STUDENT_LESSON_PROJECT_ASSIGNMENT, { 
      studentId, 
      CourseID: courseId 
    });
  }

  async getStudentLessonActivity(studentId, courseId) {
    return this.post(API_CONFIG.ENDPOINTS.GET_STUDENT_LESSON_ACTIVITY, { 
      studentId, 
      CourseID: courseId 
    });
  }

  async uploadStudentLessonProject(formData) {
    return this.uploadFile(API_CONFIG.ENDPOINTS.UPLOAD_STUDENT_LESSON_PROJECT, formData);
  }

  // Rewards System Methods
  async getRewardsForStudents() {
    return this.get(API_CONFIG.ENDPOINTS.GET_REWARDS_FOR_STUDENTS);
  }

  async getStudentRedeemHistory() {
    return this.get(API_CONFIG.ENDPOINTS.STUDENT_REDEEM_HISTORY);
  }

  async redeemGift(rewardData) {
    return this.post(API_CONFIG.ENDPOINTS.REDEEM_GIFT, rewardData);
  }

  // Fees and Payments Methods
  async getBankDetails() {
    return this.get(API_CONFIG.ENDPOINTS.BANK_DETAILS);
  }

  async getDiscounts() {
    return this.get(API_CONFIG.ENDPOINTS.GET_DISCOUNTS);
  }

  async generateInvoiceNumber() {
    return this.get(API_CONFIG.ENDPOINTS.GENERATE_INVOICE_NUMBER);
  }

  async generateInvoice(formData) {
    return this.uploadFile(API_CONFIG.ENDPOINTS.GENERATE_INVOICE, formData);
  }

  async getAllStudentsInvoiceDetails() {
    return this.get(API_CONFIG.ENDPOINTS.ALL_STUDENTS_INVOICE_DETAILS);
  }

  // Email and Communication Methods
  async getEmailByUsername(username) {
    return this.post(API_CONFIG.ENDPOINTS.GET_EMAIL_BY_USERNAME, { username });
  }

  async sendEmail(emailData) {
    return this.post(API_CONFIG.ENDPOINTS.SEND_EMAIL, emailData);
  }

  async getEmailStatus(batchId) {
    return this.get(`${API_CONFIG.ENDPOINTS.EMAIL_STATUS}/${batchId}`);
  }

  async sendMassEmail(emailData) {
    return this.post(API_CONFIG.ENDPOINTS.SEND_MASS_EMAIL, emailData);
  }

  // Polymatch Competition Methods
  async getPolymatchCompetition() {
    try {
      const response = await this.get(API_CONFIG.ENDPOINTS.POLYMATCH_COMPETITION);
      return response;
    } catch (error) {
      console.error('Get Polymatch Competition Error:', error);
      throw error;
    }
  }

  async getLatestPolymatchCompetition() {
    try {
      const response = await this.get(API_CONFIG.ENDPOINTS.LATEST_POLYMATCH_COMPETITION);
      return response;
    } catch (error) {
      console.error('Get Latest Polymatch Competition Error:', error);
      throw error;
    }
  }

  async getAllPolymatchCompetitions() {
    try {
      const response = await this.get(API_CONFIG.ENDPOINTS.ALL_POLYMATCH_COMPETITIONS);
      return response;
    } catch (error) {
      console.error('Get All Polymatch Competitions Error:', error);
      throw error;
    }
  }

  async registerPolymatch(formData) {
    try {
      const response = await this.uploadFile(API_CONFIG.ENDPOINTS.POLYMATCH_REGISTER, formData);
      return response;
    } catch (error) {
      console.error('Polymatch Registration Error:', error);
      throw error;
    }
  }

  async getPolymatchResults(competitionId) {
    try {
      const response = await this.get(`${API_CONFIG.ENDPOINTS.POLYMATCH_RESULTS}/${competitionId}`);
      return response;
    } catch (error) {
      console.error('Get Polymatch Results Error:', error);
      throw error;
    }
  }

  // File Management Methods
  async getInvoiceFile(fileName) {
    return buildBackendUrl(`${API_CONFIG.ENDPOINTS.GET_INVOICE_FILE}/${fileName}`);
  }

  async getUserImage(avatar) {
    return buildBackendUrl(`${API_CONFIG.ENDPOINTS.GET_UIMAGE}/${avatar}`);
  }

  // Lesson Activity Profile Methods
  async getLessonActivityProfileData(studentId, courseId) {
    return this.get(`${API_CONFIG.ENDPOINTS.GET_LESSON_ACTIVITY_PROFILE_DATA}/${studentId}/${courseId}`);
  }

  // Contact Form Methods
  async submitContactForm(contactData) {
    return this.post(API_CONFIG.ENDPOINTS.CONTACT, contactData);
  }

  // Newsletter Subscription Methods
  async addNewsletterEmail(email) {
    try {
      return await this.post(API_CONFIG.ENDPOINTS.ADD_NEWSLETTER_EMAIL, { email });
    } catch (error) {
      // Handle 409 Conflict (already subscribed)
      if (error.status === 409 || (error.message && error.message.includes('409'))) {
        throw new Error('This email is already subscribed!');
      }
      // If backend returns plain text instead of JSON
      if (error.responseText || (error.message && error.message.startsWith('Email'))) {
        throw new Error(error.responseText || error.message);
      }
      throw error;
    }
  }

  // Utility method to check if user is authenticated
  async checkAuthStatus() {
    try {
      const user = await this.getCurrentUser();
      return { isAuthenticated: true, user };
    } catch (error) {
      return { isAuthenticated: false, user: null };
    }
  }

  async registerInStudentTable(userId) {
    try {
      const response = await this.post(API_CONFIG.ENDPOINTS.REGISTER_IN_STUDENT_TABLE, { user_id: userId });
      return response;
    } catch (error) {
      console.error('Register Student Error:', error);
      throw error;
    }
  }

  async checkUniqueUsername(username) {
    try {
      const response = await this.post(API_CONFIG.ENDPOINTS.CHECK_UNIQUE_USERNAME, { username });
      return response;
    } catch (error) {
      console.error('Check Username Error:', error);
      throw error;
    }
  }

  // Islamic Competition Results
  async getStudentsResultsIsl() {
    return this.get(API_CONFIG.ENDPOINTS.STUDENTS_RESULTS_ISL);
  }

  // English Competition Results
  async getStudentsResultsEng() {
    return this.get(API_CONFIG.ENDPOINTS.STUDENTS_RESULTS_ENG);
  }
}

// Create and export a singleton instance
const apiService = new ApiService();
export default apiService; 